import CheckIcon from "@mui/icons-material/Check";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import type { AlertColor } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import axios from "axios";
import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

import {
  namirialSignboxStart,
  namirialOneshotStartData,
  namirialGetStatus,
  namirialOneshotFinish,
  namirialOneshotGenerateOtp,
  namirialOneshotResendOtp,
  namirialGetByLinkCodeData,
} from "../../../api/signer";
import type { ResponseNamirialSignature, NamirialSignatureStatus } from "../../../types/namirial";

type NamirialStep =
  | "choose"
  | "signbox-form"
  | "oneshot-form"
  | "oneshot-identity"
  | "oneshot-otp"
  | "oneshot-generating-otp"
  | "oneshot-signing"
  | "processing"
  | "error";

const MAX_IDENTITY_AUTO_RESTART = 3;
const POLL_SHORT_MS = 10000;
const POLL_LONG_MS = 10000;
const MAX_RETRIES_SHORT = 40;
const MAX_RETRIES_LONG = 500;

const NAMIRIAL_IDENTITY_IN_PROGRESS_CODE = "S1219";
const NAMIRIAL_SIGNER_ALREADY_VERIFIED_CODE = "S1218";

interface NamirialSignatureModalProps {
  id: string;
  open: boolean;
  onClose: () => void;
  setMessageAlert: (msg: string) => void;
  setTypeAlert: (type: AlertColor) => void;
  setOpenAlert: (open: boolean) => void;
  onNamirialIdentityAwaitingChange?: (awaiting: boolean) => void;
  onSuccess?: () => void;
}

interface NamirialError {
  error?: string;
  message?: string;
  code?: string;
}

function getAxiosResponseErrorMessage(data: unknown, fallback: string): string {
  if (!data || typeof data !== "object") {
    return fallback;
  }
  const d = data as Record<string, unknown>;
  const message =
    typeof d.message === "string" && d.message.trim() !== ""
      ? d.message.trim()
      : undefined;
  const err =
    typeof d.error === "string" && d.error.trim() !== ""
      ? d.error.trim()
      : undefined;
  if (message) {
    return message;
  }
  if (err) {
    return err;
  }
  return fallback;
}

function isIdentityAlreadyInProgressError(e: unknown): boolean {
  if (!axios.isAxiosError(e)) {
    return false;
  }
  const res = e.response;
  if (!res || res.status !== 400) {
    return false;
  }
  const raw: unknown = res.data;
  if (typeof raw === "object" && raw !== null && "code" in raw) {
    const rec = raw as Record<string, unknown>;
    if (rec.code === NAMIRIAL_IDENTITY_IN_PROGRESS_CODE) {
      return true;
    }
  }
  const msg = getAxiosResponseErrorMessage(raw, "").toLowerCase();
  return msg.includes("identity verification in progress");
}

function isSignerAlreadyVerifiedError(e: unknown): boolean {
  if (!axios.isAxiosError(e)) {
    return false;
  }
  const res = e.response;
  if (!res || res.status !== 400) {
    return false;
  }
  const raw: unknown = res.data;
  if (typeof raw === "object" && raw !== null && "code" in raw) {
    const rec = raw as Record<string, unknown>;
    if (rec.code === NAMIRIAL_SIGNER_ALREADY_VERIFIED_CODE) {
      return true;
    }
  }
  const msg = getAxiosResponseErrorMessage(raw, "").toLowerCase();
  return msg.includes("signer was already verified");
}

interface ResumeOneshotFromLinkCodeDeps {
  envelopeId: string;
  linkCode: string;
  t: (key: string) => string;
  handleError: (message: string) => void;
  handleSuccess: () => void;
  setConfigId: (id: string) => void;
  setVideoIdLinkOneShot: (url: string) => void;
  setIdentityAwaiting: (awaiting: boolean) => void;
  setStep: (step: NamirialStep) => void;
  setProcessingMessage: (msg: string) => void;
  pollIdentityThenContinue: (envelopeId: string, cfgId: string) => void;
  pollStatus: (
    envelopeId: string,
    cfgId: string,
    targetStatus: "COMPLETED" | "WAITING_OTP",
    onTarget: () => void,
    retries?: number,
    intervalMs?: number,
  ) => void;
}

async function resumeNamirialOneshotFromLinkCode(
  deps: ResumeOneshotFromLinkCodeDeps,
): Promise<void> {
  const {
    envelopeId,
    linkCode,
    t,
    handleError,
    handleSuccess,
    setConfigId,
    setVideoIdLinkOneShot,
    setIdentityAwaiting,
    setStep,
    setProcessingMessage,
    pollIdentityThenContinue,
    pollStatus,
  } = deps;
  try {
    const data = await namirialGetByLinkCodeData(envelopeId, linkCode);
    setConfigId(data.id);
    setVideoIdLinkOneShot(data.videoIdLinkOneShot ?? "");
    if (data.status === "NAMIRIAL_AWAITING_IDENTITY_VERIFICATION") {
      setIdentityAwaiting(true);
      setStep("oneshot-identity");
      setProcessingMessage("");
      pollIdentityThenContinue(envelopeId, data.id);
      return;
    }

    if (data.status === "NAMIRIAL_IDENTITY_VERIFIED") {
      setIdentityAwaiting(false);
      setStep("oneshot-generating-otp");
      setProcessingMessage(t("namirial.generatingOtp"));
      try {
        await namirialOneshotGenerateOtp(envelopeId, data.id);
      } catch (e) {
        if (axios.isAxiosError<NamirialError>(e)) {
          const msg = getAxiosResponseErrorMessage(
            e.response?.data,
            t("namirial.generateOtpError"),
          );
          handleError(msg);
        } else {
          handleError(t("namirial.defaultError"));
        }
        return;
      }

      pollStatus(
        envelopeId,
        data.id,
        "WAITING_OTP",
        () => {
          setStep("oneshot-otp");
        },
        MAX_RETRIES_SHORT,
        POLL_SHORT_MS,
      );
      return;
    }

    if (data.status === "GENERATING_OTP") {
      setIdentityAwaiting(false);
      setStep("oneshot-generating-otp");
      setProcessingMessage(t("namirial.generatingOtp"));
      pollStatus(
        envelopeId,
        data.id,
        "WAITING_OTP",
        () => {
          setStep("oneshot-otp");
        },
        MAX_RETRIES_SHORT,
        POLL_SHORT_MS,
      );
      return;
    }

    if (data.status === "SIGNING") {
      setIdentityAwaiting(false);
      setStep("oneshot-signing");
      setProcessingMessage(t("namirial.processingSignature"));
      pollStatus(
        envelopeId,
        data.id,
        "COMPLETED",
        () => {
          handleSuccess();
        },
      );
      return;
    }
    handleError(t("namirial.resumeLinkCodeUnexpected"));
  } catch {
    handleError(t("namirial.resumeLinkCodeFailed"));
  }
}

export default function NamirialSignatureModal({
  open,
  onClose,
  setMessageAlert,
  setOpenAlert,
  setTypeAlert,
  id,
  onNamirialIdentityAwaitingChange,
  onSuccess,
}: NamirialSignatureModalProps): React.JSX.Element {
  const params = useParams() as { hash: string };
  const { t } = useTranslation();

  const [step, setStep] = useState<NamirialStep>("choose");

  const [certUsername, setCertUsername] = useState("");
  const [certPassword, setCertPassword] = useState("");
  const [certPin, setCertPin] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPin, setShowPin] = useState(false);

  const [otp, setOtp] = useState("");

  const [configId, setConfigId] = useState("");
  const [videoIdLinkOneShot, setVideoIdLinkOneShot] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [processingMessage, setProcessingMessage] = useState("");
  const [isResendingOtp, setIsResendingOtp] = useState(false);

  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const identityRestartCountRef = useRef(0);
  const handleOneshotStartRef = useRef<
    ((mode?: "full" | "resumeOnly") => Promise<void>) | null
  >(null);
  const hasCheckedRef = useRef(false);

  const clearPolling = useCallback((): void => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const setIdentityAwaiting = useCallback(
    (awaiting: boolean): void => {
      onNamirialIdentityAwaitingChange?.(awaiting);
    },
    [onNamirialIdentityAwaitingChange],
  );

  const handleReset = useCallback((): void => {
    setStep("choose");
    setCertUsername("");
    setCertPassword("");
    setCertPin("");
    setShowPassword(false);
    setShowPin(false);
    setOtp("");
    setConfigId("");
    setVideoIdLinkOneShot("");
    setErrorMessage("");
    setProcessingMessage("");
    setIsResendingOtp(false);
    identityRestartCountRef.current = 0;
    clearPolling();
  }, [clearPolling]);

  const handleSuccess = useCallback((): void => {
    clearPolling();
    setIdentityAwaiting(false);
    setTypeAlert("success");
    setMessageAlert(t("chooseSignature.success"));
    setOpenAlert(true);
    handleReset();
    onClose();
    onSuccess?.();
  }, [
    clearPolling,
    setIdentityAwaiting,
    setTypeAlert,
    setMessageAlert,
    setOpenAlert,
    handleReset,
    onClose,
    onSuccess,
    t,
  ]);

  const handleError = useCallback(
    (message: string): void => {
      clearPolling();
      setIdentityAwaiting(false);
      setErrorMessage(message);
      setStep("error");
    },
    [clearPolling, setIdentityAwaiting],
  );

  const pollStatus = useCallback(
    (
      envelopeId: string,
      cfgId: string,
      targetStatus: "COMPLETED" | "WAITING_OTP",
      onTarget: () => void,
      retries = MAX_RETRIES_SHORT,
      intervalMs = POLL_SHORT_MS,
    ): void => {
      if (retries <= 0) {
        handleError(t("namirial.timeoutAwaitingSignature"));
        return;
      }

      pollingRef.current = setTimeout(() => {
        void (async () => {
          try {
            const { data } = await namirialGetStatus(envelopeId, cfgId);

            if (data.status === "ERROR") {
              handleError(t("namirial.signatureError"));
              return;
            }

            // OTP inválido: parar o polling e voltar ao formulário OTP com mensagem de erro
            if (data.status === "INVALID_OTP") {
              clearPolling();
              setOtp("");
              setErrorMessage(t("namirial.invalidOtp"));
              setStep("oneshot-otp");
              return;
            }

            if (data.status === targetStatus) {
              onTarget();
              return;
            }

            if (data.status === "COMPLETED") {
              handleSuccess();
              return;
            }

            pollStatus(
              envelopeId,
              cfgId,
              targetStatus,
              onTarget,
              retries - 1,
              intervalMs,
            );
          } catch {
            handleError(t("namirial.statusCheckError"));
          }
        })();
      }, intervalMs);
    },
    [clearPolling, handleError, handleSuccess, t],
  );

  const restartOneshotIdentity = useCallback(
    (
      envelopeId: string,
      linkCode: string,
    ): Promise<ResponseNamirialSignature> =>
      namirialOneshotStartData(envelopeId, {
        linkCode,
        hasCarimboTempo: false,
      }),
    [],
  );

  const pollIdentityThenContinue = useCallback(
    (envelopeId: string, cfgId: string, retries = MAX_RETRIES_LONG): void => {
      if (retries <= 0) {
        handleError(t("namirial.timeoutAwaitingIdentity"));
        return;
      }

      pollingRef.current = setTimeout(() => {
        void (async () => {
          try {
            const { data } = await namirialGetStatus(envelopeId, cfgId);

            if (data.status === "NAMIRIAL_IDENTITY_VERIFIED") {
              setIdentityAwaiting(false);
              setStep("oneshot-generating-otp");
              setProcessingMessage(t("namirial.generatingOtp"));
              try {
                await namirialOneshotGenerateOtp(envelopeId, cfgId);
              } catch (e) {
                if (axios.isAxiosError<NamirialError>(e)) {
                  const msg = getAxiosResponseErrorMessage(
                    e.response?.data,
                    t("namirial.generateOtpError"),
                  );
                  handleError(msg);
                } else {
                  handleError(t("namirial.defaultError"));
                }
                return;
              }

              pollStatus(
                envelopeId,
                cfgId,
                "WAITING_OTP",
                () => {
                  setStep("oneshot-otp");
                },
                MAX_RETRIES_SHORT,
                POLL_SHORT_MS,
              );
              return;
            }

            if (
              data.status === "NAMIRIAL_IDENTITY_VERIFICATION_ERROR" ||
              data.status === "ERROR"
            ) {
              if (identityRestartCountRef.current < MAX_IDENTITY_AUTO_RESTART) {
                identityRestartCountRef.current += 1;
                setProcessingMessage(t("namirial.restartingIdentity"));
                setStep("processing");
                try {
                  const next = await restartOneshotIdentity(
                    envelopeId,
                    params.hash,
                  );
                  setConfigId(next.id);
                  setVideoIdLinkOneShot(next.videoIdLinkOneShot ?? "");
                  setIdentityAwaiting(true);
                  setStep("oneshot-identity");
                  pollIdentityThenContinue(envelopeId, next.id);
                } catch (e) {
                  if (
                    isIdentityAlreadyInProgressError(e) ||
                    isSignerAlreadyVerifiedError(e)
                  ) {
                    await handleOneshotStartRef.current?.("full");
                    return;
                  }
                  if (axios.isAxiosError<NamirialError>(e)) {
                    const msg = getAxiosResponseErrorMessage(
                      e.response?.data,
                      t("namirial.restartIdentityError"),
                    );
                    handleError(msg);
                  } else {
                    handleError(t("namirial.defaultError"));
                  }
                }
                return;
              }

              handleError(
                data.error || t("namirial.identityVerificationFailed"),
              );
              return;
            }

            pollIdentityThenContinue(envelopeId, cfgId, retries - 1);
          } catch {
            handleError(t("namirial.identityStatusCheckError"));
          }
        })();
      }, POLL_LONG_MS);
    },
    [
      handleError,
      params.hash,
      pollStatus,
      restartOneshotIdentity,
      setIdentityAwaiting,
      t,
    ],
  );

  const handleSignboxSubmit = async (): Promise<void> => {
    if (!certUsername.trim() || !certPassword.trim() || !certPin.trim()) {
      setErrorMessage(t("namirial.fillAllFields"));
      return;
    }

    try {
      setErrorMessage("");
      setStep("processing");
      setProcessingMessage(t("namirial.startingSignbox"));

      const { data } = await namirialSignboxStart(id, {
        linkCode: params.hash,
        certUsername: certUsername.trim(),
        certPassword: certPassword.trim(),
        certPin: certPin.trim(),
        hasCarimboTempo: false,
      });

      setConfigId(data.id);
      setProcessingMessage(t("namirial.processingSignature"));

      pollStatus(id, data.id, "COMPLETED", () => {
        handleSuccess();
      });
    } catch (e) {
      if (axios.isAxiosError<NamirialError>(e)) {
        const msg = getAxiosResponseErrorMessage(
          e.response?.data,
          t("namirial.signboxStartError"),
        );
        handleError(msg);
      } else {
        handleError(t("namirial.defaultError"));
      }
    }
  };

  const handleOneshotStart = async (
    mode: "full" | "resumeOnly" = "full",
  ): Promise<void> => {
    if (mode === "resumeOnly") {
      await resumeNamirialOneshotFromLinkCode({
        envelopeId: id,
        linkCode: params.hash,
        t,
        handleError,
        handleSuccess,
        setConfigId,
        setVideoIdLinkOneShot,
        setIdentityAwaiting,
        setStep,
        setProcessingMessage,
        pollIdentityThenContinue,
        pollStatus,
      });
      return;
    }

    try {
      setErrorMessage("");
      identityRestartCountRef.current = 0;
      setStep("processing");
      setProcessingMessage(t("namirial.startingIdentity"));

      const data = await namirialOneshotStartData(id, {
        linkCode: params.hash,
        hasCarimboTempo: false,
      });

      setConfigId(data.id);
      setVideoIdLinkOneShot(data.videoIdLinkOneShot ?? "");
      setIdentityAwaiting(
        data.status === "NAMIRIAL_AWAITING_IDENTITY_VERIFICATION",
      );
      setStep("oneshot-identity");
      setProcessingMessage("");

      pollIdentityThenContinue(id, data.id);
    } catch (e) {
      if (
        isIdentityAlreadyInProgressError(e) ||
        isSignerAlreadyVerifiedError(e)
      ) {
        await handleOneshotStart("resumeOnly");
        return;
      }
      if (axios.isAxiosError<NamirialError>(e)) {
        const msg = getAxiosResponseErrorMessage(
          e.response?.data,
          t("namirial.startIdentityError"),
        );
        handleError(msg);
      } else {
        handleError(t("namirial.defaultError"));
      }
    }
  };

  handleOneshotStartRef.current = handleOneshotStart;

  const handleOtpSubmit = async (): Promise<void> => {
    if (!otp.trim()) {
      setErrorMessage(t("namirial.informOtp"));
      return;
    }

    try {
      setErrorMessage("");
      setStep("oneshot-signing");
      setProcessingMessage(t("namirial.finishingSignature"));

      await namirialOneshotFinish(id, configId, {
        otp: otp.trim(),
      });

      pollStatus(id, configId, "COMPLETED", () => {
        handleSuccess();
      });
    } catch (e) {
      if (axios.isAxiosError<NamirialError>(e)) {
        if (e.response?.status === 400 || e.response?.status === 404) {
          setStep("oneshot-otp");
          setOtp("");
          setErrorMessage(t("namirial.invalidOtp"));
          return;
        }
        const msg = getAxiosResponseErrorMessage(
          e.response?.data,
          t("namirial.finishSignatureError"),
        );
        handleError(msg);
      } else {
        handleError(t("namirial.defaultError"));
      }
    }
  };

  const handleClose = (): void => {
    handleReset();
    onClose();
  };

  useEffect(() => {
    if (open) {
      if (hasCheckedRef.current) return;
      hasCheckedRef.current = true;

      void (async () => {
        try {
          setStep("processing");
          setProcessingMessage(t("namirial.processingMsg"));

          const data = await namirialGetByLinkCodeData(id, params.hash);
          if (data && data.id) {
            const inProgressStatuses: NamirialSignatureStatus[] = [
              "STARTED",
              "WAITING_OTP",
              "INVALID_OTP",
              "SIGNING",
              "NAMIRIAL_AWAITING_IDENTITY_VERIFICATION",
              "NAMIRIAL_IDENTITY_VERIFIED",
              "GENERATING_OTP",
            ];

            if (inProgressStatuses.includes(data.status)) {
              setConfigId(data.id);
              setVideoIdLinkOneShot(data.videoIdLinkOneShot ?? "");

              if (data.api === "ONESHOT") {
                if (data.status === "NAMIRIAL_AWAITING_IDENTITY_VERIFICATION") {
                  setIdentityAwaiting(true);
                  setStep("oneshot-identity");
                  setProcessingMessage("");
                  pollIdentityThenContinue(id, data.id);
                } else if (data.status === "NAMIRIAL_IDENTITY_VERIFIED") {
                  setIdentityAwaiting(false);
                  setStep("oneshot-generating-otp");
                  setProcessingMessage(t("namirial.generatingOtp"));
                  try {
                    await namirialOneshotGenerateOtp(id, data.id);
                    pollStatus(
                      id,
                      data.id,
                      "WAITING_OTP",
                      () => {
                        setStep("oneshot-otp");
                      },
                      MAX_RETRIES_SHORT,
                      POLL_SHORT_MS,
                    );
                  } catch (e) {
                    if (axios.isAxiosError<NamirialError>(e)) {
                      const msg = getAxiosResponseErrorMessage(
                        e.response?.data,
                        t("namirial.generateOtpError"),
                      );
                      handleError(msg);
                    } else {
                      handleError(t("namirial.defaultError"));
                    }
                  }
                } else if (data.status === "GENERATING_OTP") {
                  setIdentityAwaiting(false);
                  setStep("oneshot-generating-otp");
                  setProcessingMessage(t("namirial.generatingOtp"));
                  pollStatus(
                    id,
                    data.id,
                    "WAITING_OTP",
                    () => {
                      setStep("oneshot-otp");
                    },
                    MAX_RETRIES_SHORT,
                    POLL_SHORT_MS,
                  );
                } else if (data.status === "WAITING_OTP" || data.status === "INVALID_OTP") {
                  setIdentityAwaiting(false);
                  setStep("oneshot-otp");
                  setProcessingMessage("");
                } else if (data.status === "SIGNING") {
                  setIdentityAwaiting(false);
                  setStep("oneshot-signing");
                  setProcessingMessage(t("namirial.processingSignature"));
                  pollStatus(id, data.id, "COMPLETED", () => {
                    handleSuccess();
                  });
                } else {
                  setIdentityAwaiting(false);
                  setStep("processing");
                  setProcessingMessage(t("namirial.processingSignature"));
                  pollStatus(id, data.id, "COMPLETED", () => {
                    handleSuccess();
                  });
                }
              } else if (data.api === "SIGNBOX") {
                setStep("processing");
                setProcessingMessage(t("namirial.processingSignature"));
                pollStatus(id, data.id, "COMPLETED", () => {
                  handleSuccess();
                });
              } else {
                setStep("choose");
              }
              return;
            }
          }
          setStep("choose");
        } catch {
          setStep("choose");
        }
      })();
    } else {
      hasCheckedRef.current = false;
      clearPolling();
    }
  }, [
    open,
    id,
    params.hash,
    clearPolling,
    pollIdentityThenContinue,
    pollStatus,
    setIdentityAwaiting,
    t,
    handleError,
    handleSuccess,
  ]);

  const renderStepper = (activeStep: 1 | 2 | 3): JSX.Element => {
    const getStepCircle = (stepNum: 1 | 2 | 3) => {
      const isCompleted = stepNum < activeStep;
      const isActive = stepNum === activeStep;

      if (isCompleted) {
        return (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: "#4CAF50",
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <CheckIcon sx={{ fontSize: 20 }} />
          </Box>
        );
      }

      return (
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            backgroundColor: isActive ? "primary.main" : "#EAEAEA",
            color: isActive ? "#FFFFFF" : "#777777",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
            fontSize: 16,
            mb: 1,
          }}
        >
          {stepNum}
        </Box>
      );
    };

    const getStepText = (stepNum: 1 | 2 | 3) => {
      const isActive = stepNum === activeStep;

      let label = "";
      if (stepNum === 1) {
        label = stepNum < activeStep ? t("namirial.stepIdentitySuccess") : t("namirial.stepIdentity");
      } else if (stepNum === 2) {
        label = stepNum < activeStep ? t("namirial.stepCertificateSuccess") : t("namirial.stepCertificate");
      } else if (stepNum === 3) {
        label = isActive ? t("namirial.stepSignatureActive") : t("namirial.stepSignature");
      }

      return (
        <Typography
          sx={{
            fontWeight: isActive ? "600" : "400",
            fontSize: 12,
            color: isActive ? "text.primary" : "text.secondary",
            textAlign: "center",
          }}
        >
          {label}
        </Typography>
      );
    };

    const getConnectorColor = (connectorNum: 1 | 2) => {
      return activeStep > connectorNum ? "#4CAF50" : "#D3D3D3";
    };

    return (
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "center", width: "100%", py: 1 }}>
        {/* Step 1 */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 120 }}>
          {getStepCircle(1)}
          {getStepText(1)}
        </Box>

        {/* Connector 1 */}
        <Box sx={{ flexGrow: 1, height: "1px", backgroundColor: getConnectorColor(1), mt: "18px", mx: 1 }} />

        {/* Step 2 */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 120 }}>
          {getStepCircle(2)}
          {getStepText(2)}
        </Box>

        {/* Connector 2 */}
        <Box sx={{ flexGrow: 1, height: "1px", backgroundColor: getConnectorColor(2), mt: "18px", mx: 1 }} />

        {/* Step 3 */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 120 }}>
          {getStepCircle(3)}
          {getStepText(3)}
        </Box>
      </Box>
    );
  };

  const renderChoose = (): JSX.Element => (
    <>
      <DialogTitle sx={{ fontWeight: "500", fontSize: 24, pb: 0 }}>
        {t("namirial.paraguayTitle")}
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}
      >
        <Typography
          sx={{ fontWeight: "400", fontSize: 14, color: "action.active" }}
        >
          {t("namirial.selectMethod")}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{ padding: "16px 24px", flexDirection: "column", gap: 2 }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Button
            color="primary"
            onClick={() => {
              setStep("signbox-form");
            }}
            sx={{ borderRadius: "12px", width: "100%" }}
            variant="contained"
          >
            {t("namirial.hasCertificate")}
          </Button>
          <Button
            color="primary"
            onClick={() => {
              setStep("oneshot-form");
            }}
            sx={{ borderRadius: "12px", width: "100%" }}
            variant="outlined"
          >
            {t("namirial.noCertificate")}
          </Button>
        </Box>
        <Button
          color="primary"
          onClick={handleClose}
          sx={{ borderRadius: "12px", width: "100%" }}
        >
          {t("chooseSignature.back")}
        </Button>
      </DialogActions>
    </>
  );

  const renderSignboxForm = (): JSX.Element => (
    <>
      <DialogTitle sx={{ fontWeight: "500", fontSize: 24, pb: 0 }}>
        {t("namirial.signWithCertTitle")}
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}
      >
        <Typography
          sx={{ fontWeight: "400", fontSize: 14, color: "action.active" }}
        >
          {t("namirial.certCredentialsIntro")}
        </Typography>
        <TextField
          fullWidth
          label={t("namirial.certUsername")}
          onChange={(e) => {
            setCertUsername(e.target.value);
            if (errorMessage) setErrorMessage("");
          }}
          value={certUsername}
          variant="outlined"
        />
        <TextField
          fullWidth
          label={t("namirial.certPassword")}
          onChange={(e) => {
            setCertPassword(e.target.value);
            if (errorMessage) setErrorMessage("");
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => {
                      setShowPassword(!showPassword);
                    }}
                    size="small"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          type={showPassword ? "text" : "password"}
          value={certPassword}
          variant="outlined"
        />
        <TextField
          fullWidth
          label={t("namirial.certPin")}
          onChange={(e) => {
            setCertPin(e.target.value);
            if (errorMessage) setErrorMessage("");
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => {
                      setShowPin(!showPin);
                    }}
                    size="small"
                  >
                    {showPin ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          type={showPin ? "text" : "password"}
          value={certPin}
          variant="outlined"
        />
        {errorMessage ? (
          <Typography color="error" sx={{ fontSize: 13 }}>
            {errorMessage}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions
        sx={{ padding: "16px 24px", flexDirection: "column", gap: 2 }}
      >
        <Button
          color="primary"
          disabled={!certUsername || !certPassword || !certPin}
          onClick={() => void handleSignboxSubmit()}
          sx={{ borderRadius: "12px", width: "100%" }}
          variant="contained"
        >
          {t("namirial.confirmSignature")}
        </Button>
        <Button
          color="primary"
          onClick={() => {
            setErrorMessage("");
            setStep("choose");
          }}
          sx={{ borderRadius: "12px", width: "100%" }}
        >
          {t("namirial.changeSignatureType")}
        </Button>
      </DialogActions>
    </>
  );

  const renderOneshotForm = (): JSX.Element => (
    <>
      <DialogTitle sx={{ fontWeight: "500", fontSize: 24, pb: 0 }}>
        {t("namirial.oneshotTitle")}
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}
      >
        <Typography
          sx={{ fontWeight: "400", fontSize: 14, color: "action.active" }}
        >
          {t("namirial.oneshotIdentityIntro")}
        </Typography>
        {errorMessage ? (
          <Typography color="error" sx={{ fontSize: 13 }}>
            {errorMessage}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions
        sx={{ padding: "16px 24px", flexDirection: "column", gap: 2 }}
      >
        <Button
          color="primary"
          onClick={() => void handleOneshotStart()}
          sx={{ borderRadius: "12px", width: "100%" }}
          variant="contained"
        >
          {t("namirial.startIdentityVerification")}
        </Button>
        <Button
          color="primary"
          onClick={() => {
            setErrorMessage("");
            setStep("choose");
          }}
          sx={{ borderRadius: "12px", width: "100%" }}
        >
          {t("namirial.changeSignatureType")}
        </Button>
      </DialogActions>
    </>
  );

  const renderOneshotIdentity = (): JSX.Element => (
    <>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          pt: 1,
          pb: 2,
        }}
      >
        {/* Stepper Header */}
        {renderStepper(1)}

        {/* QR Code Container */}
        {videoIdLinkOneShot ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              mt: 1,
            }}
          >
            <Typography sx={{ fontWeight: "700", fontSize: 24, textAlign: "center", mb: 1 }}>
              {t("namirial.scanQrCode")}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.secondary", textAlign: "center", mb: 3 }}>
              {t("namirial.useSmartphone")}
            </Typography>
            <Box
              sx={{
                p: 2,
                backgroundColor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                borderRadius: "16px",
                boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.06)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 2,
              }}
            >
              <QRCodeSVG value={videoIdLinkOneShot} size={180} />
            </Box>
          </Box>
        ) : null}

        {/* Awaiting Verification Info Box */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            backgroundColor: "#F0F2FD",
            border: "1px solid #D6DCFC",
            borderRadius: "12px",
            p: 2,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <CircularProgress size={20} />
          <Typography sx={{ fontSize: 14, fontWeight: "500", color: "#3B4675" }}>
            {t("namirial.awaitingNamirialApproval")}
          </Typography>
        </Box>

        {/* Instruction Message */}
        <Typography
          sx={{
            fontWeight: "400",
            fontSize: 12,
            color: "text.secondary",
            textAlign: "center",
          }}
        >
          {t("namirial.identityDoNotClose")}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px",
          display: "flex",
          gap: 2,
          justifyContent: "center",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Button
          color="primary"
          onClick={() => {
            if (videoIdLinkOneShot) {
              window.open(
                videoIdLinkOneShot,
                "NamirialVerification",
                "width=850,height=666,top=50,left=50,resizable=yes,scrollbars=yes,status=no,location=no"
              );
            }
          }}
          disabled={!videoIdLinkOneShot}
          sx={{ borderRadius: "12px", flex: 1, py: 1.5 }}
          variant="contained"
        >
          {t("namirial.continueOnSmartphone")}
        </Button>
        <Button
          color="inherit"
          onClick={handleClose}
          sx={{
            borderRadius: "12px",
            flex: 1,
            py: 1.5,
            border: "1px solid #D1D5DB",
            "&:hover": {
              backgroundColor: "#F3F4F6",
            },
          }}
          variant="outlined"
        >
          {t("namirial.cancel")}
        </Button>
      </DialogActions>
    </>
  );

  const handleResendOtp = async (): Promise<void> => {
    if (isResendingOtp) return;
    try {
      setIsResendingOtp(true);
      setErrorMessage("");
      await namirialOneshotResendOtp(id, configId);
    } catch (e) {
      if (axios.isAxiosError<NamirialError>(e)) {
        const msg = getAxiosResponseErrorMessage(
          e.response?.data,
          t("namirial.resendOtpError"),
        );
        setErrorMessage(msg);
      } else {
        setErrorMessage(t("namirial.resendOtpError"));
      }
    } finally {
      setIsResendingOtp(false);
    }
  };

  const renderOtpForm = (): JSX.Element => (
    <>
      <DialogTitle sx={{ fontWeight: "500", fontSize: 24, pb: 0 }}>
        {t("namirial.otpTitle")}
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}
      >
        {renderStepper(3)}
        <Typography
          sx={{ fontWeight: "400", fontSize: 14, color: "action.active" }}
        >
          {t("namirial.otpDescriptionSms")}
        </Typography>
        <TextField
          error={Boolean(errorMessage)}
          fullWidth
          helperText={errorMessage || ""}
          label={t("namirial.otpLabel")}
          onChange={(e) => {
            setOtp(e.target.value);
            if (errorMessage) setErrorMessage("");
          }}
          placeholder="000000"
          value={otp}
          variant="outlined"
        />
      </DialogContent>
      <DialogActions
        sx={{ padding: "16px 24px", flexDirection: "column", gap: 2 }}
      >
        <Button
          color="primary"
          disabled={!otp || isResendingOtp}
          onClick={() => void handleOtpSubmit()}
          sx={{ borderRadius: "12px", width: "100%" }}
          variant="contained"
        >
          {t("namirial.confirmSignature")}
        </Button>
        <Button
          color="primary"
          disabled={isResendingOtp}
          onClick={() => void handleResendOtp()}
          sx={{ borderRadius: "12px", width: "100%" }}
          variant="outlined"
        >
          {isResendingOtp ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} />
              {t("namirial.resendingOtp")}
            </Box>
          ) : (
            t("namirial.resendOtp")
          )}
        </Button>
        <Button
          color="primary"
          onClick={handleClose}
          sx={{ borderRadius: "12px", width: "100%" }}
        >
          {t("namirial.cancel")}
        </Button>
      </DialogActions>
    </>
  );

  const renderProcessing = (): JSX.Element => (
    <>
      <DialogTitle sx={{ fontWeight: "500", fontSize: 24, pb: 0 }}>
        {t("namirial.processing")}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 2,
            py: 4,
          }}
        >
          <CircularProgress />
          <Typography sx={{ fontWeight: "400", fontSize: 14, textAlign: "center" }}>
            {processingMessage || t("namirial.processingMsg")}
          </Typography>
        </Box>
      </DialogContent>
    </>
  );

  const renderOneshotGeneratingOtp = (): JSX.Element => (
    <>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          pt: 2,
          pb: 2,
        }}
      >
        {renderStepper(3)}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 2,
            py: 4,
          }}
        >
          <CircularProgress />
          <Typography sx={{ fontWeight: "400", fontSize: 14, textAlign: "center" }}>
            {processingMessage || t("namirial.processingMsg")}
          </Typography>
        </Box>
      </DialogContent>
    </>
  );

  const renderOneshotSigning = (): JSX.Element => (
    <>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          pt: 2,
          pb: 2,
        }}
      >
        {renderStepper(3)}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 2,
            py: 4,
          }}
        >
          <CircularProgress />
          <Typography sx={{ fontWeight: "400", fontSize: 14, textAlign: "center" }}>
            {processingMessage || t("namirial.processingMsg")}
          </Typography>
        </Box>
      </DialogContent>
    </>
  );

  const renderError = (): JSX.Element => (
    <>
      <DialogTitle sx={{ fontWeight: "500", fontSize: 24, pb: 0 }}>
        {t("namirial.error")}
      </DialogTitle>
      <DialogContent>
        <Typography
          color="error"
          sx={{ fontWeight: "400", fontSize: 14, mt: 2 }}
        >
          {errorMessage || t("namirial.defaultError")}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{ padding: "16px 24px", flexDirection: "column", gap: 2 }}
      >
        <Button
          color="primary"
          onClick={() => {
            handleReset();
          }}
          sx={{ borderRadius: "12px", width: "100%" }}
          variant="contained"
        >
          {t("namirial.retry")}
        </Button>
        <Button
          color="primary"
          onClick={handleClose}
          sx={{ borderRadius: "12px", width: "100%" }}
        >
          {t("namirial.back")}
        </Button>
      </DialogActions>
    </>
  );

  const renderStep = (): JSX.Element => {
    switch (step) {
      case "choose":
        return renderChoose();
      case "signbox-form":
        return renderSignboxForm();
      case "oneshot-form":
        return renderOneshotForm();
      case "oneshot-identity":
        return renderOneshotIdentity();
      case "oneshot-generating-otp":
        return renderOneshotGeneratingOtp();
      case "oneshot-signing":
        return renderOneshotSigning();
      case "oneshot-otp":
        return renderOtpForm();
      case "processing":
        return renderProcessing();
      case "error":
        return renderError();
      default:
        return renderChoose();
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={handleClose} open={open}>
      {renderStep()}
    </Dialog>
  );
}
